import React, { useState, useEffect } from 'react';
import { 
  Building2, Shield, AlertTriangle, CheckCircle, Clock, 
  Plus, Trash2, FileText, TrendingUp, Search, X, Edit2,
  RefreshCw, ChevronDown, ChevronRight, ExternalLink,
  Globe, Mail, Phone, Calendar, AlertCircle
} from 'lucide-react';
import { vendorsAPI } from '../lib/api';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

const VENDOR_CATEGORIES = [
  { value: 'software', label: 'Software/SaaS' },
  { value: 'cloud', label: 'Cloud Services' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'services', label: 'Professional Services' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'other', label: 'Other' }
];

const CRITICALITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-blue-500' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-500' },
  { value: 'high', label: 'High', color: 'bg-orange-500' },
  { value: 'critical', label: 'Critical', color: 'bg-red-500' }
];

const RISK_COLORS = {
  low: '#22c55e',
  medium: '#eab308',
  high: '#f97316',
  critical: '#ef4444'
};

const VendorRiskDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [questionnaire, setQuestionnaire] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showVendorDetail, setShowVendorDetail] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [assessmentResponses, setAssessmentResponses] = useState({});
  
  // Form state
  const [vendorForm, setVendorForm] = useState({
    name: '',
    description: '',
    category: 'software',
    criticality: 'medium',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    contractStartDate: '',
    contractEndDate: '',
    contractValue: 0,
    dataAccess: [],
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, vendorsRes, questionnaireRes] = await Promise.all([
        vendorsAPI.getStats(),
        vendorsAPI.getAll(),
        vendorsAPI.getQuestionnaire()
      ]);
      setStats(statsRes.data);
      setVendors(vendorsRes.data);
      setQuestionnaire(questionnaireRes.data);
    } catch (error) {
      console.error('Error loading vendor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    try {
      await vendorsAPI.create(vendorForm);
      setShowAddVendor(false);
      resetVendorForm();
      loadData();
    } catch (error) {
      console.error('Error creating vendor:', error);
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Delete this vendor and all assessments?')) return;
    try {
      await vendorsAPI.delete(vendorId);
      loadData();
    } catch (error) {
      console.error('Error deleting vendor:', error);
    }
  };

  const openVendorDetail = async (vendor) => {
    try {
      const res = await vendorsAPI.get(vendor.id);
      setSelectedVendor(res.data);
      setShowVendorDetail(true);
    } catch (error) {
      console.error('Error fetching vendor:', error);
    }
  };

  const startAssessment = async (vendor) => {
    try {
      // Create a new assessment
      const res = await vendorsAPI.createAssessment(vendor.id, { assessmentType: 'standard' });
      setSelectedVendor({ ...vendor, currentAssessment: res.data });
      setAssessmentResponses({});
      setShowAssessment(true);
    } catch (error) {
      console.error('Error creating assessment:', error);
    }
  };

  const handleSubmitAssessment = async () => {
    if (!selectedVendor?.currentAssessment) return;
    try {
      await vendorsAPI.updateAssessment(
        selectedVendor.id, 
        selectedVendor.currentAssessment.id,
        { responses: assessmentResponses, status: 'completed' }
      );
      setShowAssessment(false);
      setSelectedVendor(null);
      setAssessmentResponses({});
      loadData();
    } catch (error) {
      console.error('Error submitting assessment:', error);
    }
  };

  const resetVendorForm = () => {
    setVendorForm({
      name: '',
      description: '',
      category: 'software',
      criticality: 'medium',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      website: '',
      contractStartDate: '',
      contractEndDate: '',
      contractValue: 0,
      dataAccess: [],
      notes: ''
    });
  };

  const filteredVendors = vendors.filter(v =>
    v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRiskColor = (level) => RISK_COLORS[level] || RISK_COLORS.medium;
  const getRiskBadgeClass = (level) => {
    const classes = {
      low: 'bg-green-500/20 text-green-400',
      medium: 'bg-yellow-500/20 text-yellow-400',
      high: 'bg-orange-500/20 text-orange-400',
      critical: 'bg-red-500/20 text-red-400'
    };
    return classes[level] || classes.medium;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  const riskDistributionData = stats ? [
    { name: 'Low', value: stats.riskDistribution?.low || 0, color: RISK_COLORS.low },
    { name: 'Medium', value: stats.riskDistribution?.medium || 0, color: RISK_COLORS.medium },
    { name: 'High', value: stats.riskDistribution?.high || 0, color: RISK_COLORS.high },
    { name: 'Critical', value: stats.riskDistribution?.critical || 0, color: RISK_COLORS.critical }
  ] : [];

  const categoryData = stats?.categoryDistribution ? 
    Object.entries(stats.categoryDistribution).map(([name, value]) => ({ name, value })) : [];

  return (
    <div data-testid="vendor-risk-dashboard" className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              Vendor Risk Management
            </h1>
            <p className="text-slate-400 mt-2">Assess and monitor third-party vendor security risks</p>
          </div>
          <button
            onClick={() => setShowAddVendor(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
            data-testid="add-vendor-btn"
          >
            <Plus className="w-4 h-4" />
            Add Vendor
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Total Vendors</p>
                  <p className="text-2xl font-bold text-white">{stats.totalVendors}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Active</p>
                  <p className="text-2xl font-bold text-white">{stats.activeVendors}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">High/Critical Risk</p>
                  <p className="text-2xl font-bold text-white">
                    {(stats.riskDistribution?.high || 0) + (stats.riskDistribution?.critical || 0)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Expiring Contracts</p>
                  <p className="text-2xl font-bold text-white">{stats.expiringContracts}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Avg Risk Score</p>
                  <p className="text-2xl font-bold text-white">{stats.averageRiskScore}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-2">
          {['overview', 'vendors', 'assessments'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-t-lg font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-amber-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risk Distribution */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Risk Distribution</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={riskDistributionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                    >
                      {riskDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-4 mt-4 flex-wrap">
                {riskDistributionData.map((item) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                    <span className="text-slate-400 text-sm">{item.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Distribution */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Vendors by Category</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={categoryData} layout="vertical">
                    <XAxis type="number" stroke="#64748b" />
                    <YAxis dataKey="name" type="category" stroke="#64748b" width={100} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                      labelStyle={{ color: '#f8fafc' }}
                    />
                    <Bar dataKey="value" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* High Risk Vendors */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 lg:col-span-2">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                High Risk Vendors
              </h3>
              {vendors.filter(v => v.riskLevel === 'high' || v.riskLevel === 'critical').length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle className="w-10 h-10 mx-auto text-green-500 mb-2" />
                  <p className="text-slate-400">No high-risk vendors</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {vendors.filter(v => v.riskLevel === 'high' || v.riskLevel === 'critical').map((vendor) => (
                    <div 
                      key={vendor.id} 
                      className={`p-4 rounded-lg border cursor-pointer hover:bg-slate-700/50 transition-colors ${
                        vendor.riskLevel === 'critical' ? 'bg-red-500/10 border-red-500/30' : 'bg-orange-500/10 border-orange-500/30'
                      }`}
                      onClick={() => openVendorDetail(vendor)}
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="text-white font-medium">{vendor.name}</h4>
                        <span className={`px-2 py-1 rounded text-xs ${getRiskBadgeClass(vendor.riskLevel)}`}>
                          {vendor.riskLevel}
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm mt-1">{vendor.category}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-slate-500 text-sm">Risk Score</span>
                        <span className={`font-bold ${vendor.riskLevel === 'critical' ? 'text-red-400' : 'text-orange-400'}`}>
                          {vendor.riskScore}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Vendors Tab */}
        {activeTab === 'vendors' && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Vendor Directory</h3>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search vendors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            {filteredVendors.length === 0 ? (
              <div className="p-12 text-center">
                <Building2 className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                <p className="text-slate-400">No vendors found</p>
                <button
                  onClick={() => setShowAddVendor(true)}
                  className="mt-3 text-amber-400 hover:text-amber-300"
                >
                  Add your first vendor
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-700/50">
                      <th className="text-left p-4 text-slate-400 font-medium">Vendor</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Category</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Criticality</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Risk Level</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Last Assessment</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVendors.map((vendor) => (
                      <tr key={vendor.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                        <td className="p-4">
                          <div 
                            className="cursor-pointer"
                            onClick={() => openVendorDetail(vendor)}
                          >
                            <p className="text-white font-medium hover:text-amber-400">{vendor.name}</p>
                            <p className="text-slate-400 text-sm">{vendor.contactEmail || 'No contact'}</p>
                          </div>
                        </td>
                        <td className="p-4 text-slate-300 capitalize">{vendor.category}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs text-white ${
                            CRITICALITY_OPTIONS.find(c => c.value === vendor.criticality)?.color || 'bg-slate-500'
                          }`}>
                            {vendor.criticality}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${getRiskBadgeClass(vendor.riskLevel)}`}>
                            {vendor.riskLevel || 'Not assessed'}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            vendor.status === 'active' ? 'bg-green-500/20 text-green-400' :
                            vendor.status === 'under_review' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-slate-500/20 text-slate-400'
                          }`}>
                            {vendor.status}
                          </span>
                        </td>
                        <td className="p-4 text-slate-400 text-sm">
                          {vendor.lastAssessmentDate ? new Date(vendor.lastAssessmentDate).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => startAssessment(vendor)}
                              className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                              title="Start Assessment"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteVendor(vendor.id)}
                              className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Assessments Tab */}
        {activeTab === 'assessments' && (
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">Security Questionnaire</h3>
            <p className="text-slate-400 mb-6">
              Use this standardized questionnaire to assess vendor security posture. 
              Click "Start Assessment" on any vendor to begin.
            </p>
            <div className="space-y-4">
              {questionnaire.map((q, idx) => (
                <div key={q.id} className="p-4 bg-slate-700/50 rounded-lg">
                  <div className="flex items-start gap-4">
                    <span className="text-amber-400 font-mono text-sm">{idx + 1}.</span>
                    <div>
                      <p className="text-white">{q.question}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-slate-400">Category: {q.category}</span>
                        <span className="text-slate-400">Weight: {q.weight}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Vendor Modal */}
      {showAddVendor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-slate-800 rounded-xl w-full max-w-2xl border border-slate-700 my-8">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Add New Vendor</h3>
              <button onClick={() => setShowAddVendor(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateVendor} className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-slate-300 text-sm mb-1">Vendor Name *</label>
                  <input
                    type="text"
                    value={vendorForm.name}
                    onChange={(e) => setVendorForm({...vendorForm, name: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Category</label>
                  <select
                    value={vendorForm.category}
                    onChange={(e) => setVendorForm({...vendorForm, category: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    {VENDOR_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Criticality</label>
                  <select
                    value={vendorForm.criticality}
                    onChange={(e) => setVendorForm({...vendorForm, criticality: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    {CRITICALITY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-slate-300 text-sm mb-1">Description</label>
                  <textarea
                    value={vendorForm.description}
                    onChange={(e) => setVendorForm({...vendorForm, description: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Contact Name</label>
                  <input
                    type="text"
                    value={vendorForm.contactName}
                    onChange={(e) => setVendorForm({...vendorForm, contactName: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Contact Email</label>
                  <input
                    type="email"
                    value={vendorForm.contactEmail}
                    onChange={(e) => setVendorForm({...vendorForm, contactEmail: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Website</label>
                  <input
                    type="url"
                    value={vendorForm.website}
                    onChange={(e) => setVendorForm({...vendorForm, website: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="https://"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Contract Value ($)</label>
                  <input
                    type="number"
                    value={vendorForm.contractValue}
                    onChange={(e) => setVendorForm({...vendorForm, contractValue: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Contract Start</label>
                  <input
                    type="date"
                    value={vendorForm.contractStartDate}
                    onChange={(e) => setVendorForm({...vendorForm, contractStartDate: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Contract End</label>
                  <input
                    type="date"
                    value={vendorForm.contractEndDate}
                    onChange={(e) => setVendorForm({...vendorForm, contractEndDate: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddVendor(false)}
                  className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg"
                >
                  Add Vendor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vendor Detail Modal */}
      {showVendorDetail && selectedVendor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl w-full max-w-2xl border border-slate-700 max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800">
              <div>
                <h3 className="text-xl font-semibold text-white">{selectedVendor.name}</h3>
                <p className="text-slate-400 text-sm capitalize">{selectedVendor.category}</p>
              </div>
              <button onClick={() => setShowVendorDetail(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Risk Score */}
              <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                <div>
                  <p className="text-slate-400 text-sm">Risk Score</p>
                  <p className={`text-3xl font-bold ${
                    selectedVendor.riskLevel === 'critical' ? 'text-red-400' :
                    selectedVendor.riskLevel === 'high' ? 'text-orange-400' :
                    selectedVendor.riskLevel === 'medium' ? 'text-yellow-400' : 'text-green-400'
                  }`}>{selectedVendor.riskScore || 'N/A'}</p>
                </div>
                <span className={`px-3 py-1 rounded-lg ${getRiskBadgeClass(selectedVendor.riskLevel)}`}>
                  {selectedVendor.riskLevel || 'Not Assessed'}
                </span>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="text-white font-medium mb-3">Contact Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  {selectedVendor.contactName && (
                    <div className="flex items-center gap-2 text-slate-300">
                      <Building2 className="w-4 h-4 text-slate-500" />
                      {selectedVendor.contactName}
                    </div>
                  )}
                  {selectedVendor.contactEmail && (
                    <div className="flex items-center gap-2 text-slate-300">
                      <Mail className="w-4 h-4 text-slate-500" />
                      {selectedVendor.contactEmail}
                    </div>
                  )}
                  {selectedVendor.website && (
                    <a 
                      href={selectedVendor.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-amber-400 hover:text-amber-300"
                    >
                      <Globe className="w-4 h-4" />
                      Website
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>

              {/* Contract Info */}
              {(selectedVendor.contractStartDate || selectedVendor.contractEndDate) && (
                <div>
                  <h4 className="text-white font-medium mb-3">Contract Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-400">Start Date</p>
                      <p className="text-white">{selectedVendor.contractStartDate || 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">End Date</p>
                      <p className="text-white">{selectedVendor.contractEndDate || 'Not set'}</p>
                    </div>
                    {selectedVendor.contractValue > 0 && (
                      <div>
                        <p className="text-slate-400">Value</p>
                        <p className="text-white">${selectedVendor.contractValue.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {selectedVendor.certifications?.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-3">Certifications</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedVendor.certifications.map((cert, idx) => (
                      <span key={idx} className="px-3 py-1 bg-green-500/20 text-green-400 rounded-lg text-sm">
                        {cert.name}
                        {cert.verified && <CheckCircle className="w-3 h-3 inline ml-1" />}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowVendorDetail(false);
                    startAssessment(selectedVendor);
                  }}
                  className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Start Assessment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assessment Modal */}
      {showAssessment && selectedVendor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl w-full max-w-3xl border border-slate-700 max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-xl font-semibold text-white">Security Assessment</h3>
                <p className="text-slate-400 text-sm">{selectedVendor.name}</p>
              </div>
              <button onClick={() => setShowAssessment(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {questionnaire.map((q, idx) => (
                <div key={q.id} className="p-4 bg-slate-700/50 rounded-lg">
                  <div className="flex items-start gap-4">
                    <span className="text-amber-400 font-mono text-sm mt-1">{idx + 1}.</span>
                    <div className="flex-1">
                      <p className="text-white mb-3">{q.question}</p>
                      <div className="flex gap-3">
                        {['yes', 'partial', 'no'].map((answer) => (
                          <button
                            key={answer}
                            onClick={() => setAssessmentResponses({...assessmentResponses, [q.id]: answer})}
                            className={`px-4 py-2 rounded-lg capitalize transition-colors ${
                              assessmentResponses[q.id] === answer
                                ? answer === 'yes' ? 'bg-green-500 text-white' :
                                  answer === 'partial' ? 'bg-yellow-500 text-black' :
                                  'bg-red-500 text-white'
                                : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                            }`}
                          >
                            {answer}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-slate-700 flex-shrink-0">
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-400">
                  {Object.keys(assessmentResponses).length} of {questionnaire.length} answered
                </span>
                <div className="h-2 w-48 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 transition-all"
                    style={{ width: `${(Object.keys(assessmentResponses).length / questionnaire.length) * 100}%` }}
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAssessment(false)}
                  className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitAssessment}
                  disabled={Object.keys(assessmentResponses).length < questionnaire.length}
                  className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-600 text-white rounded-lg"
                >
                  Submit Assessment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorRiskDashboard;
